import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HrService } from '../../../services/human-resources/hr.service';
import { NotificationService } from '../../../services/human-resources/notification.service';
import {
  NotificationRecord,
  NotificationType,
  NotificationSettings,
  NotificationChannel,
  Employee
} from '../../../models/hr-models';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {
  notificationSettings$: Observable<NotificationSettings>;
  notifications$: Observable<any[]>;
  employees$: Observable<Employee[]>;

  // For settings form
  documentExpiryDays: string = '';
  birthdayReminderDays: number = 3;
  anniversaryReminderDays: number = 5;
  notificationChannel = NotificationChannel.Both;
  hrEmails: string = '';

  notificationChannels = Object.values(NotificationChannel);

  constructor(
    private hrService: HrService,
    private notificationService: NotificationService
  ) {
    this.notificationSettings$ = this.hrService.notificationSettings$;
    this.employees$ = this.hrService.employees$;

    // Load notifications (This would normally come from a dedicated endpoint)
    // For now we'll simulate with document notifications
    this.notifications$ = this.hrService.documents$.pipe(
      map(documents => {
        // Flatten all notifications from all documents
        return documents
          .filter(doc => doc.notificationsSent && doc.notificationsSent.length > 0)
          .flatMap(doc => {
            return doc.notificationsSent.map(notification => {
              return {
                ...notification,
                documentTitle: doc.title,
                documentType: doc.documentType
              };
            });
          })
          .sort((a, b) => {
            // Sort by date, most recent first
            return new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime();
          });
      })
    );
  }

  ngOnInit(): void {
    this.hrService.refreshData();

    // Initialize form values from settings
    this.notificationSettings$.subscribe(settings => {
      this.documentExpiryDays = settings.documentExpiryReminderDays.join(', ');
      this.birthdayReminderDays = settings.birthdayReminderDays;
      this.anniversaryReminderDays = settings.anniversaryReminderDays;
      this.notificationChannel = settings.defaultNotificationChannel;
      this.hrEmails = settings.hrEmailAddresses.join(', ');
    });
  }

  /**
   * Save notification settings
   */
  saveSettings(): void {
    const documentExpiryReminderDays = this.documentExpiryDays
      .split(',')
      .map(day => parseInt(day.trim()))
      .filter(day => !isNaN(day) && day > 0)
      .sort((a, b) => b - a); // Sort descending

    const hrEmailAddresses = this.hrEmails
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const settings: NotificationSettings = {
      documentExpiryReminderDays,
      birthdayReminderDays: this.birthdayReminderDays,
      anniversaryReminderDays: this.anniversaryReminderDays,
      defaultNotificationChannel: this.notificationChannel,
      hrEmailAddresses
    };

    this.hrService.updateNotificationSettings(settings).subscribe(
      updatedSettings => {
        alert('Notification settings updated successfully!');
      },
      error => {
        console.error('Error updating notification settings:', error);
        alert('Error updating notification settings.');
      }
    );
  }

  /**
   * Test notifications by sending immediate notifications
   */
  testNotifications(): void {
    this.notificationService.checkForNotifications().subscribe(
      sent => {
        if (sent) {
          alert('Test notifications sent successfully!');
        } else {
          alert('No notifications to send at this time.');
        }
      },
      error => {
        console.error('Error sending test notifications:', error);
        alert('Error sending test notifications.');
      }
    );
  }

  /**
   * Get a readable label for notification type
   */
  getNotificationTypeLabel(type: NotificationType): string {
    switch (type) {
      case NotificationType.DocumentExpiring:
        return 'Document Expiring';
      case NotificationType.DocumentExpired:
        return 'Document Expired';
      case NotificationType.Birthday:
        return 'Birthday';
      case NotificationType.WorkAnniversary:
        return 'Work Anniversary';
      default:
        return type;
    }
  }

  /**
   * Get channel label
   */
  getChannelLabel(channel: NotificationChannel): string {
    switch (channel) {
      case NotificationChannel.Email:
        return 'Email';
      case NotificationChannel.SystemPush:
        return 'System Push';
      case NotificationChannel.Both:
        return 'Email & System Push';
      default:
        return channel;
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: Date): string {
    return new Date(date).toLocaleString();
  }

  /**
   * Get employee name by ID
   */
  getEmployeeName(employeeId: number): Observable<string> {
    return this.employees$.pipe(
      map(employees => {
        const employee = employees.find(e => e.id === employeeId);
        return employee ? employee.name : 'Unknown';
      })
    );
  }
}
